"use client";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import clsx from "clsx";

import type { StrapiDetailedSlide } from "@/shared/api/strapi";
import { useHorizontalSlider } from "@/shared/lib/slider/useHorizontalSlider";
import { Icon } from "@/shared/ui/icon/Icon";
import { StrapiImage } from "@/shared/ui/strapi-image";

import styles from "./DetailedSlider.module.css";

export interface DetailedSliderProps {
  initialSlide?: number;
  showNavButtons?: boolean;
  slides?: StrapiDetailedSlide[];
  className?: string;
  variant?: "transparent" | "secondary" | "cyan" | "orange" | "success";
}

export function DetailedSlider({
  initialSlide = 0,
  showNavButtons = true,
  slides,
  className,
  variant = "transparent",
}: DetailedSliderProps) {
  const slidesData = slides || [];
  const totalSlides = slidesData.length;
  const { currentSlide, sliderRef, goToSlide } = useHorizontalSlider(
    totalSlides,
    initialSlide,
  );

  if (totalSlides === 0) {
    return null;
  }

  return (
    <div className={clsx(styles.sliderWrapper, className, styles[variant])}>
      {showNavButtons && (
        <div className={styles.navButtons} role="tablist">
          {slidesData.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={clsx(
                styles.navBtn,
                currentSlide === index && styles.active,
              )}
              onClick={() => goToSlide(index)}
              aria-label={`Перейти к слайду ${slide?.label}`}
              aria-controls={`slide-${index}`}
              role="tab"
              aria-selected={currentSlide === index}
            >
              {slide.iconName && <Icon name={slide.iconName} />}
              {slide?.label || ""}
            </button>
          ))}
        </div>
      )}

      <div className={styles.sliderBox}>
        <div
          className={styles.sliderContainer}
          ref={sliderRef}
          role="region"
          aria-label="Слайдер с изображениями"
        >
          <div className={styles.slides}>
            {slidesData.map((slide, index) => (
              <div
                key={slide.id || index}
                id={`slide-${index}`}
                className={styles.slide}
                role="tabpanel"
                aria-label={`Слайд ${slide?.label}`}
                aria-hidden={currentSlide !== index}
              >
                <div className={styles.slideContent}>
                  {slide?.content?.map((content, contentIndex) => {
                    const headingBlock = content.text.find(
                      (block) => block.type === "heading",
                    );
                    const bodyBlocks = content.text.filter(
                      (block) => block.type !== "heading",
                    );
                    const amountSlideContent = slide?.content?.length ?? 0;

                    return (
                      <div key={content.id} className={styles.slideContentGroup}>
                        {headingBlock && (
                          <div className={styles.slideContentHeading}>
                            <BlocksRenderer content={[headingBlock]} />
                            <div className={styles.slideContentIndicator}>
                              {Array.from({ length: amountSlideContent }).map(
                                (_, i) => (
                                  <div
                                    key={`indicator-${content.id}-${i}`}
                                    className={
                                      i === contentIndex ? styles.active : undefined
                                    }
                                  />
                                ),
                              )}
                            </div>
                          </div>
                        )}
                        {bodyBlocks.length > 0 && (
                          <div className={styles.slideContentBody}>
                            <BlocksRenderer content={bodyBlocks} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <StrapiImage
                  image={slide.image}
                  width={900}
                  height={573}
                  className={styles.slideImage}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
